import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { onFeatureLocked } from '../../utils/subscriptionModals'
import { useAccount } from '../../hooks/useAccount'
import './FeatureLockedModal.css'

/**
 * Modal displayed when user tries to access a locked feature
 * Listens to global feature-locked events from API interceptor
 *
 * Phase 3: Now shows account context in error message (P2 enhancement)
 */
const FeatureLockedModal = () => {
  const { t } = useTranslation('subscription')
  const [isOpen, setIsOpen] = useState(false)
  const [data, setData] = useState(null)
  const navigate = useNavigate()

  // Phase 3: Get current account context for better error messaging
  const { selectedAccount } = useAccount()

  // Feature configuration with details - moved inside component to access t()
  const FEATURE_DETAILS = {
    gif_rendering: {
      name: t('featureLocked.features.gif_rendering.name'),
      icon: t('featureLocked.features.gif_rendering.icon'),
      description: t('featureLocked.features.gif_rendering.description'),
      availableIn: [
        { id: 'pro', name: 'Pro', price: t('featureLocked.planPricing.pro'), badge: 'popular' },
        { id: 'gif_addon', name: 'GIF Add-on', price: '€5/mo', badge: 'addon' }
      ]
    },
    video_upload: {
      name: t('featureLocked.features.videoUpload.name'),
      icon: t('featureLocked.features.videoUpload.icon'),
      description: t('featureLocked.features.videoUpload.description'),
      availableIn: [
        { id: 'starter', name: 'Starter', price: t('featureLocked.planPricing.starter'), badge: 'value' },
        { id: 'pro', name: 'Pro', price: t('featureLocked.planPricing.pro'), badge: 'popular' }
      ]
    },
    advanced_analytics: {
      name: t('featureLocked.features.advancedAnalytics.name'),
      icon: t('featureLocked.features.advancedAnalytics.icon'),
      description: t('featureLocked.features.advancedAnalytics.description'),
      availableIn: [
        { id: 'pro', name: 'Pro', price: t('featureLocked.planPricing.pro'), badge: 'popular' }
      ]
    },
    unlimited_stories: {
      name: t('featureLocked.features.unlimitedStories.name'),
      icon: t('featureLocked.features.unlimitedStories.icon'),
      description: t('featureLocked.features.unlimitedStories.description'),
      availableIn: [
        { id: 'pro', name: 'Pro', price: t('featureLocked.planPricing.pro'), badge: 'popular' }
      ]
    }
  }

  useEffect(() => {
    // Subscribe to feature locked events
    const unsubscribe = onFeatureLocked((modalData) => {
      setData(modalData)
      setIsOpen(true)
    })

    // Cleanup on unmount
    return unsubscribe
  }, [])

  if (!isOpen || !data) return null

  const handleViewPlans = () => {
    setIsOpen(false)
    navigate('/subscription', { state: { highlightFeature: data.feature_id } })
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  // Get feature details or use defaults
  const featureInfo = FEATURE_DETAILS[data.feature_id] || {
    name: data.feature_id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    icon: '🔒',
    description: t('featureLocked.defaultDescription'),
    availableIn: [
      { id: 'starter', name: 'Starter', price: t('featureLocked.planPricing.starter'), badge: 'value' },
      { id: 'pro', name: 'Pro', price: t('featureLocked.planPricing.pro'), badge: 'popular' }
    ]
  }

  return (
    <div className="modal-overlay" onClick={handleClose} role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <div className="modal-content feature-locked-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 id="modal-title">{t('featureLocked.title')}</h2>
          <button
            className="close-button"
            onClick={handleClose}
            aria-label={t('featureLocked.closeAriaLabel')}
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="feature-icon">{featureInfo.icon}</div>

          <h3 className="feature-name">{featureInfo.name}</h3>

          {/* Phase 3: Show account name if available for better context */}
          {selectedAccount && (
            <p className="text-sm text-gray-600 mb-2">
              Account: <span className="font-semibold">@{selectedAccount.instagram_username || selectedAccount.username}</span>
            </p>
          )}

          <p className="feature-message">
            {data.message || featureInfo.description}
          </p>

          <div className="feature-available-section">
            <p className="section-title">{t('featureLocked.availableIn')}</p>

            <div className="plan-list">
              {featureInfo.availableIn.map((plan) => (
                <div key={plan.id} className="plan-item">
                  <div className="plan-info">
                    <div className="plan-name-row">
                      <span className="plan-name">{plan.name}</span>
                      {plan.badge === 'popular' && (
                        <span className="plan-badge popular">{t('featureLocked.planBadges.mostPopular')}</span>
                      )}
                      {plan.badge === 'value' && (
                        <span className="plan-badge value">{t('featureLocked.planBadges.bestValue')}</span>
                      )}
                    </div>
                    <span className="plan-price">{plan.price}</span>
                  </div>
                  <div className="checkmark">{t('featureLocked.checkmark')}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="upgrade-benefits">
            <p className="benefits-title">{t('featureLocked.whyUpgrade.title')}</p>
            <ul>
              <li>{t('featureLocked.whyUpgrade.benefits.unlockFeatures')}</li>
              <li>{t('featureLocked.whyUpgrade.benefits.scaleStrategy')}</li>
            </ul>
          </div>
        </div>

        <div className="modal-footer">
          <button className="button button-secondary" onClick={handleClose}>
            {t('featureLocked.buttons.close')}
          </button>
          <button className="button button-primary" onClick={handleViewPlans}>
            {t('featureLocked.buttons.viewPlans')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default FeatureLockedModal
